import { Component, OnDestroy, OnInit } from "@angular/core";
import { Router, ActivatedRoute } from "@angular/router";
import { UntypedFormGroup, UntypedFormBuilder, Validators } from "@angular/forms";
import { Profile } from "../../shared/models/profile";
import { UserService } from "../../../shared/services/user.service";
import { AppSettingsService } from "../../../shared/services/app-settings.service";
import { ViewStateService } from "../../../shared/services/view-state.service";
import { CommonApiService } from "../../../shared/services/api/common-api.service";
import { PractitionerService } from "../../shared/services/practitioner.service";
import { NotificationsService } from "../../../shared/services/notifications.service";
import { Subject } from "rxjs";
import { takeUntil } from "rxjs/operators";

@Component({
  selector: "demographic-app-edit",
  templateUrl: "./demographic-edit.component.html",
  styleUrls: ["./demographic-edit.component.css"],
})
export class DemographicEditComponent implements OnInit, OnDestroy {
  profileForm: UntypedFormGroup;
  profile: Profile;
  formLevelErrors: String[] = [];
  raceLov: any[];
  spanishLov: any[];
  genderLov: any[];
  statesLov: any[];
  employmentStatusLov: any[];
  employmentSectorLov: any[];
  jobRoleLov: any[];
  patientTypeLov: any[];
  workSettingLov: any[];
  validFormCheck = true;
  personId: number;
  demographics: any;
  dropdownSettings = {};
  dropdownSettingsPT = {};
  reviewDemographics: boolean = false;
  credentialed: boolean = false;

  employed: boolean = false;
  workSectorRespiratory: boolean = false;

  onDestroy$ = new Subject();
  constructor(
    private formBuilder: UntypedFormBuilder,
    private route: ActivatedRoute,
    private userService: UserService,
    private viewState: ViewStateService,
    private practitionerService: PractitionerService,
    private router: Router,
    private commonApiService: CommonApiService,
    private notification: NotificationsService,
    public appSettings: AppSettingsService
  ) {}

  ngOnInit() {
    this.viewState.startLoading();
    this.getLovs();

    this.personId = this.userService.CurrentUser.personId;
    this.credentialed = this.userService.CurrentUser.credentialHolder;
    this.profile = new Profile();
    this.buildForm();

    this.dropdownSettings = {
      singleSelection: false,
      idField: "codeValue",
      textField: "codeDescription",
      enableCheckAll: false,
      selectAllText: "Select All",
      unSelectAllText: "UnSelect All",
      itemsShowLimit: 50,
      allowSearchFilter: false,
    };

    this.getProfileDetails();
  }

  employmentStatus(inValue) {
    if (inValue == 1 || inValue == 2) {
      this.employed = true;
    } else this.employed = false;
  }

  employmentSector(inValue) {
    if (inValue == 1 || inValue == 2) {
      this.workSectorRespiratory = true;
    } else this.workSectorRespiratory = false;
  }

  onSubmit(updatedFormValues: any) {
    this.viewState.startLoading();
    this.formValues = updatedFormValues;
    this.validateForm();
    this.updateDemographics();
  }
  cancel(event: Event) {
    event.preventDefault();

    this.profileForm.reset();
    this.router.navigate(["/"]);
  }

  private getProfileDetails() {
    if (this.personId) {
      this.practitionerService
        .getDemographics(this.personId.toString())
        .pipe(takeUntil(this.onDestroy$))
        .subscribe((details) => {
          this.profile = details;
          //if (!details.race) details.race="";
          //if (!details.gender) details.gender="";
          //if (!details.spanishOrigin) details.spanishOrigin="";
          this.reviewDemographics = this.profile.reviewDemographics;
          this.demographics = details;
          this.employmentStatus(details.employmentStatus);
          this.employmentSector(details.employmentSector);
          this.profileForm.patchValue(details);
          this.mapStingToBooleanValues(this.profileForm.value);
          this.viewState.stopLoading();
        });
    }
  }

  private getLovs() {
    this.commonApiService
      .getLov("RACE")
      .pipe(takeUntil(this.onDestroy$))
      .subscribe((details) => {
        this.raceLov = details;
      });
    this.commonApiService
      .getLov("GENDER")
      .pipe(takeUntil(this.onDestroy$))
      .subscribe((details) => {
        this.genderLov = details;
      });
    this.commonApiService
      .getLov("SPANISHORIGIN")
      .pipe(takeUntil(this.onDestroy$))
      .subscribe((details) => {
        this.spanishLov = details;
      });
    this.commonApiService
      .getLov("STATES")
      .pipe(takeUntil(this.onDestroy$))
      .subscribe((details) => {
        this.statesLov = details;
      });

    this.commonApiService
      .getLov("PATIENTTYPE")
      .pipe(takeUntil(this.onDestroy$))
      .subscribe((details) => {
        this.patientTypeLov = details;
      });

    this.commonApiService
      .getLov("EMPLOYMENTSECTOR")
      .pipe(takeUntil(this.onDestroy$))
      .subscribe((details) => {
        this.employmentSectorLov = details;
      });

    this.commonApiService
      .getLov("WORKSETTING")
      .pipe(takeUntil(this.onDestroy$))
      .subscribe((details) => {
        this.workSettingLov = details;
      });

    this.commonApiService
      .getLov("JOBROLE")
      .pipe(takeUntil(this.onDestroy$))
      .subscribe((details) => {
        this.jobRoleLov = details;
      });

    this.commonApiService
      .getLov("EMPLOYMENTSTATUS")
      .pipe(takeUntil(this.onDestroy$))
      .subscribe((details) => {
        this.employmentStatusLov = details;
      });
  }

  mapBooleanToStingValues(values: any) {}

  mapStingToBooleanValues(values: any) {}

  private validateForm() {
    for (const control in this.profileForm.controls) {
      this.profileForm.controls[control].updateValueAndValidity();
    }

    let hasErrors = false;
    for (const field in this.formErrors) {
      this.formErrors[field] = "";
      const control = this.profileForm.get(field);
      if (control && control.invalid) {
        hasErrors = true;
        const messages = this.validationMessages[field];
        for (const key in control.errors) {
          this.formErrors[field] += messages[key] + " ";
        }
      }
    }
    if (hasErrors) {
      this.validFormCheck = false;
      this.viewState.stopLoading();
    } else {
      this.validFormCheck = true;
    }
  }

  private buildForm(): void {
    this.profileForm = this.formBuilder.group({
      gender: [this.formValues.gender, Validators.required],
      spanishOrigin: [this.formValues.spanishOrigin, Validators.required],
      race: [this.formValues.race, Validators.required],
      employmentStatus: [this.formValues.employmentStatus],
      employmentSector: [this.formValues.employmentSector],
      employmentStates: [this.formValues.employmentStates],
      workSetting: [this.formValues.workSetting],
      patientTypes: [this.formValues.patientTypes],
      jobRole: [this.formValues.jobRole],
    });
  }

  private updateDemographics() {
    if (this.profileForm.valid) {
      this.mapBooleanToStingValues(this.profileForm.value);
      this.practitionerService
        .updateDemographics(this.personId.toString(), this.profileForm.value)
        .pipe(takeUntil(this.onDestroy$))
        .subscribe(
          (resp) => {
            this.viewState.stopLoading();
            this.notification.success(
              "Success",
              "The demographic responses have been updated."
            );
            this.router.navigate(["/"]);
          },
          (errors) => {
            this.notification.error(
              "Failed",
              "The demographic responses have not been updated."
            );
            this.mapServerErrorsToForm(errors.error);
            this.viewState.stopLoading();
          }
        );
    } else this.viewState.stopLoading();
  }

  private mapServerErrorsToForm(errorObj: any) {
    let errors = errorObj;
    errors.map((error) => {
      this.formErrors[error.fieldName] = "";
      const control = this.profileForm.get(error.fieldName);
      this.formErrors[error.fieldName] += error.message + " ";
      if (!control) {
        this.formLevelErrors.push(error.message);
      }
    });
    this.viewState.stopLoading();
  }

  private formValues = {
    gender: "",
    spanishOrigin: "",
    race: "",
    employmentStatus: "",
    employmentSector: "",
    employmentStates: "",
    workSetting: "",
    patientTypes: "",
    jobRole: "",
  };

  formErrors = {
    gender: "",
    spanishOrigin: "",
    race: "",
    employmentStatus: "",
    employmentSector: "",
    employmentStates: "",
    workSetting: "",
    patientTypes: "",
    jobRole: "",
  };

  validationMessages = {
    gender: {
      required: "Response required.",
    },
    race: {
      required: "Response required.",
    },
    spanishOrigin: {
      required: "Response required.",
    },
    employmentStatus: {},
    employmentSector: {},
    employmentStates: {},
    workSetting: {},
    patientTypes: {},
    jobRole: {},
  };

  ngOnDestroy() {
    this.onDestroy$.next();
    this.onDestroy$.complete();
  }
}
